import { Env } from "@/env";
import { authHeader } from "@features/utils";
import { MutableRefObject, useRef, useState } from "react";
import { toast } from "sonner";
import { processInChunks, UploadedPhotoType } from "./use-upload-photos";
import { v4 } from "uuid";

type UploadedVideoType = UploadedPhotoType & {
  duration?: number;
  extracted_frames?: string[]; // Array of extracted frame IDs
  frame_count?: number;
};

let counter = 0;

export const useUploadVideos = (
  subtype: string,
  uploadedFrames: MutableRefObject<any>
) => {
  const uploadedVideos = useRef<{
    [key: string]: UploadedVideoType;
  }>({});
  const [, setRefresh] = useState(0); // Used to refresh component when upload changes

  const handleVideoFile = async (newFiles: File[]) => {
    const files = Array.from(newFiles);
    for (const file of files) {
      if (!file.type.startsWith("video")) {
        toast.error("Only video files are accepted");
        continue;
      }

      const tmpId = "video_" + counter++;
      const videoUrl = URL.createObjectURL(file);

      // Create video element to get metadata
      const video = document.createElement("video");
      video.src = videoUrl;

      uploadedVideos.current = {};

      await new Promise<void>((resolve) => {
        video.onloadedmetadata = () => {
          const estimatedFrames = Math.floor(video.duration); // Roughly 1 frame per second after sharpness filtering

          if (estimatedFrames > 30) {
            toast(
              `Video is ${Math.round(
                video.duration
              )}s long. Only the first 30 sharpest frames will be used.`,
              {
                duration: 4000,
                icon: "⚠️",
              }
            );
          }

          uploadedVideos.current[tmpId] = {
            front_file: file,
            front_progress: 0,
            added_at: Date.now(),
            front_id: tmpId.toString(),
            size: { width: video.videoWidth, height: video.videoHeight },
            duration: video.duration,
            url: videoUrl,
            score: 0,
            id: "", // Will be set after frame extraction
            extracted_frames: [],
            frame_count: 0,
          };
          resolve();
        };
        video.onerror = () => {
          toast.error("Failed to load video metadata");
          resolve();
        };
      });
    }
    setRefresh((r) => r + 1);

    // Process videos to extract frames
    await processInChunks(files, 1, async (file) => {
      await toast.promise(
        async () => {
          const video = Object.values(uploadedVideos.current).find(
            (v) => v.front_file === file
          );
          if (!video || video.extracted_frames?.length) return; // Skip if already processed

          const tmpId = video.front_id;
          try {
            // Extract frames from video - 4 frames per second, keep sharpest
            const frames = await extractFramesFromVideo(file, 4);

            // Limit to maximum 30 frames
            const maxFrames = 30;
            const limitedFrames =
              frames.length > maxFrames ? frames.slice(0, maxFrames) : frames;

            if (frames.length > maxFrames) {
              toast.success(
                `Video processed: ${limitedFrames.length} frames selected (limited from ${frames.length} total frames)`
              );
            }

            uploadedVideos.current[tmpId] = {
              ...uploadedVideos.current[tmpId],
              frame_count: limitedFrames.length,
              front_progress: 0.2, // 20% progress after extraction
            };
            setRefresh((r) => r + 1);

            // Upload frames in batches of 5
            const batchSize = 5;
            const extractedFrameIds: string[] = [];

            for (
              let batchStart = 0;
              batchStart < limitedFrames.length;
              batchStart += batchSize
            ) {
              const batch = limitedFrames.slice(
                batchStart,
                Math.min(batchStart + batchSize, limitedFrames.length)
              );

              // Upload all frames in the current batch in parallel
              const batchPromises = batch.map(async (frameData, batchIndex) => {
                const globalIndex = batchStart + batchIndex;
                const uid = v4();

                const frontId = `${tmpId}_frame_${uid}`;
                uploadedFrames.current[frontId] = {
                  front_file: new File([frameData.blob], frontId, {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  }),
                  front_progress: 0,
                  added_at: Date.now(),
                  front_id: frontId,
                  size: {
                    width: video.size!.width,
                    height: video.size!.height,
                  },
                };

                try {
                  const formData = new FormData();
                  formData.append("file", frameData.blob, `frame_${uid}.jpg`);

                  const res = await fetch(
                    Env.server + "/api/loras/prepare/image?subtype=" + subtype,
                    {
                      method: "POST",
                      headers: {
                        ...authHeader(),
                      },
                      body: formData,
                    }
                  );
                  const data = await res.json();

                  if (data?.id) {
                    return {
                      id: data.id,
                      url: data.url,
                      frameIndex: globalIndex,
                      frameData,
                      ...uploadedFrames.current[frontId],
                      ...data,
                    };
                  }
                  return null;
                } catch (e) {
                  console.error(`Failed to upload frame ${globalIndex}:`, e);
                  return null;
                }
              });

              // Wait for all frames in this batch to complete
              const batchResults = await Promise.all(batchPromises);

              // Process successful uploads
              batchResults.forEach((result) => {
                if (result) {
                  extractedFrameIds.push(result.id);

                  // Add as photos now
                  uploadedFrames.current[result.front_id] = {
                    front_progress: 1,
                    size: {
                      width: video.size!.width,
                      height: video.size!.height,
                    },
                    score: result.frameData.sharpness,
                    ...result,
                  };
                }
              });

              // Update progress after each batch
              const progress =
                0.2 +
                (0.8 * Math.min(batchStart + batchSize, limitedFrames.length)) /
                  limitedFrames.length;
              uploadedVideos.current[tmpId] = {
                ...uploadedVideos.current[tmpId],
                front_progress: progress,
              };

              setRefresh((r) => r + 1);
            }

            // Mark video as complete
            uploadedVideos.current[tmpId] = {
              ...uploadedVideos.current[tmpId],
              id: `video_${tmpId}`, // Give it an ID to mark as complete
              extracted_frames: extractedFrameIds,
              front_progress: 1,
            };
          } catch (e) {
            console.error("Failed to parse video:", e);
            toast.error("Failed to process video");
            delete uploadedVideos.current[tmpId];
          }
          setRefresh((r) => r + 1);
        },
        {
          loading: "Processing video...",
          success: "Video processed",
          error: "Failed to process video",
        }
      );
    });
  };

  return { handleVideoFile, uploadedFrames };
};

// Sharpness detection functions
async function sharpnessScoreFromURL(url: string, size = 128): Promise<number> {
  const bmp = await createImageBitmap(await fetch(url).then((r) => r.blob()));
  const canvas = new OffscreenCanvas(size, size);
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) {
    throw new Error("Could not get canvas context");
  }
  const s = Math.min(size, bmp.width, bmp.height);
  ctx.drawImage(bmp, 0, 0, s, s, 0, 0, size, size);
  const { data, width, height } = ctx.getImageData(0, 0, size, size);

  // grayscale
  const gray = new Float32Array(width * height);
  for (let i = 0, j = 0; i < data.length; i += 4, j++) {
    gray[j] = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  }

  // Laplacian 3 by 3
  let sum = 0;
  let sum2 = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const v =
        -gray[i - width - 1] -
        gray[i - width] -
        gray[i - width + 1] -
        gray[i - 1] +
        8 * gray[i] -
        gray[i + 1] -
        gray[i + width - 1] -
        gray[i + width] -
        gray[i + width + 1];
      sum += v;
      sum2 += v * v;
    }
  }
  const n = (width - 2) * (height - 2);
  const mean = sum / n;
  const variance = sum2 / n - mean * mean;
  return variance; // higher is sharper
}

// Batch with small concurrency
async function sharpnessScores(urls: string[], limit = 6): Promise<number[]> {
  const out = new Array(urls.length);
  let i = 0;
  async function worker() {
    while (i < urls.length) {
      const idx = i++;
      out[idx] = await sharpnessScoreFromURL(urls[idx]);
    }
  }
  await Promise.all(
    Array.from({ length: Math.min(limit, urls.length) }, worker)
  );
  return out;
}

// Function to extract frames from video - 4 frames per second, keep sharpest
const extractFramesFromVideo = async (
  videoFile: File,
  framesPerSecond: number = 4 // Extract 4 frames per second
): Promise<{ blob: Blob; sharpness: number }[]> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      reject(new Error("Could not get canvas context"));
      return;
    }

    const videoUrl = URL.createObjectURL(videoFile);

    video.onloadedmetadata = async () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      const duration = video.duration;
      const frameInterval = 1 / framesPerSecond; // 0.25 seconds for 4fps
      const totalFrames = Math.floor(duration * framesPerSecond);

      // Extract all frames first
      const allFrames: { blob: Blob; url: string; second: number }[] = [];

      for (let i = 0; i < totalFrames; i++) {
        const currentTime = i * frameInterval;

        try {
          await new Promise<void>((seekResolve, seekReject) => {
            const handleSeeked = () => {
              video.removeEventListener("seeked", handleSeeked);
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    const url = URL.createObjectURL(blob);
                    const second = Math.floor(currentTime);
                    allFrames.push({ blob, url, second });
                  }
                  seekResolve();
                },
                "image/jpeg",
                0.8
              );
            };

            video.addEventListener("seeked", handleSeeked);
            video.currentTime = currentTime;

            // Timeout fallback
            setTimeout(() => {
              video.removeEventListener("seeked", handleSeeked);
              seekReject(new Error("Seek timeout"));
            }, 1000);
          });
        } catch (e) {
          console.warn(`Failed to extract frame at ${currentTime}s:`, e);
        }
      }

      // Group frames by second and find sharpest in each group
      const framesBySecond = new Map<number, typeof allFrames>();
      for (const frame of allFrames) {
        if (!framesBySecond.has(frame.second)) {
          framesBySecond.set(frame.second, []);
        }
        framesBySecond.get(frame.second)!.push(frame);
      }

      const sharpestFrames: { blob: Blob; sharpness: number }[] = [];

      // Process each second group
      for (const [second, frames] of Array.from(framesBySecond.entries())) {
        if (frames.length === 0) continue;

        try {
          // Calculate sharpness for all frames in this second
          const urls = frames.map((f: any) => f.url);
          const scores = await sharpnessScores(urls);

          // Find the sharpest frame
          let maxSharpness = -1;
          let sharpestIndex = 0;

          for (let i = 0; i < scores.length; i++) {
            if (scores[i] > maxSharpness) {
              maxSharpness = scores[i];
              sharpestIndex = i;
            }
          }

          // Keep only the sharpest frame
          sharpestFrames.push({
            blob: frames[sharpestIndex].blob,
            sharpness: maxSharpness,
          });

          // Clean up URLs for frames we're not keeping
          frames.forEach((frame: any, index: number) => {
            if (index !== sharpestIndex) {
              URL.revokeObjectURL(frame.url);
            }
          });

          // Clean up the kept frame URL too
          URL.revokeObjectURL(frames[sharpestIndex].url);
        } catch (e) {
          console.warn(
            `Failed to calculate sharpness for second ${second}:`,
            e
          );
          // Fallback: just take the first frame
          sharpestFrames.push({
            blob: frames[0].blob,
            sharpness: 0,
          });

          // Clean up URLs
          frames.forEach((frame: any) => URL.revokeObjectURL(frame.url));
        }
      }

      URL.revokeObjectURL(videoUrl);
      resolve(sharpestFrames);
    };

    video.onerror = () => {
      URL.revokeObjectURL(videoUrl);
      reject(new Error("Error loading video"));
    };

    video.src = videoUrl;
  });
};
